// Used by the welcome tour's anchored steps (WelcomeTour.vue) to spotlight a real DOM element with
// consistent placement/cut-out rules.

// Targets on the right half of the viewport get the panel on their left (and vice-versa) so
// it always sits beside the element and stays on-screen. "-start" top-aligns the panel with the
// element so it drops downward — fine next to top-bar targets, but for a bottom-nav target (e.g.
// the profile button) there's no room below: the panel top-aligns with an element that's already
// near the bottom edge and runs off-screen. preventOverflow's altAxis clamp (WelcomeTour.vue's own
// popper modifiers) can't fully correct that — confirmed empirically the shift it applies
// still leaves the panel overflowing the viewport bottom. "-end" bottom-aligns the panel with the
// element instead, so it grows upward — for a bottom-half target that's the direction with room.
export const placementForElement = (elementId: string): "left-start" | "left-end" | "right-start" | "right-end" => {
  const element = document.getElementById(elementId);
  if (!element) return "right-start";
  const rect = element.getBoundingClientRect();
  const side = rect.left + rect.width / 2 > window.innerWidth / 2 ? "left" : "right";
  const alignment = rect.top + rect.height / 2 > window.innerHeight / 2 ? "end" : "start";
  return `${side}-${alignment}`;
};

// Returns the spotlight cut-out's corner radius so it always matches the highlighted element's own
// shape: a rounded-square target's cut-out corners line up with its own (instead of a fixed 8px
// default), and a circular/pill target (e.g. the profile avatar, `rounded-full h-8 w-8`) gets a true
// circular cut-out. `padding` must be the same value passed to the wrapper's own overlay.padding —
// the cut-out is drawn that many px outside the element, so the radius has to grow by the same
// amount to still meet the element's edges cleanly. Returns null only when the element is missing or
// has no size to measure.
const QUADRATIC_CORNER_FACTOR = 1.17;

export const spotlightRadius = (elementId: string, padding: number): number | null => {
  const element = document.getElementById(elementId);
  if (!element) return null;
  const { width, height } = element.getBoundingClientRect();
  const half = Math.min(width, height) / 2;
  if (!half) return null;
  // Treat the target as circular/pill when its corner radius reaches half its smaller side, so the
  // cut-out is round (not a rounded square). Tailwind v4 `rounded-full` is `calc(infinity * 1px)`,
  // which getComputedStyle reports as a non-numeric value (parseFloat → NaN) — hence the NaN guard
  // alongside the "%" and numeric checks.
  const computedRadius = getComputedStyle(element).borderRadius;
  const numericRadius = parseFloat(computedRadius);
  const isCircular = computedRadius.includes("%") || Number.isNaN(numericRadius) || numericRadius >= half;
  // v-onboarding draws each cut-out corner as a quadratic curve with its control point at the corner,
  // which passes closer to the corner than a circular arc of the same radius and reads about 15%
  // tighter than the element's own rounding; the factor makes the two look alike.
  const cornerRadius = isCircular ? half : Math.min(numericRadius * QUADRATIC_CORNER_FACTOR, half);
  return cornerRadius + padding;
};

// A circle cannot be cut out of the dim: the library draws corners as quadratic curves, and a
// quadratic "circle" bulges past the button at the diagonals. Round targets get a deliberate
// rounded-square window with room around them instead of a near miss.
const CIRCLE_WINDOW_PADDING = 6;
const CIRCLE_WINDOW_RADIUS = 12;

export const spotlightOverlay = (elementId: string, padding: number): { padding: number; borderRadius: number } | null => {
  const element = document.getElementById(elementId);
  if (!element) return null;
  const { width, height } = element.getBoundingClientRect();
  const half = Math.min(width, height) / 2;
  if (!half) return null;
  const computedRadius = getComputedStyle(element).borderRadius;
  const numericRadius = parseFloat(computedRadius);
  const isCircular = computedRadius.includes("%") || Number.isNaN(numericRadius) || numericRadius >= half;
  if (isCircular) return { padding: CIRCLE_WINDOW_PADDING, borderRadius: CIRCLE_WINDOW_RADIUS };
  const radius = spotlightRadius(elementId, padding);
  return radius === null ? null : { padding, borderRadius: radius };
};
